import React from 'react';
import { Card, Typography, Switch } from 'antd';
import { Calendar, Video } from 'lucide-react';
import { CalendarWorkflowActions } from './CalendarWorkflowAction';
import { ECalendar } from 'src/enums/ECalendar';
import { CalendarWorkflowComponentProps } from '@/interfaces/ICalendarWorkflows';
import ZoomLogo from '@/assets/images/calendars/zoom.png';
import GoogleMeetLogo from '@/assets/images/calendars/meet.png';
import CalComLogo from '@/assets/images/calendars/calcom.png';

const { Text } = Typography;

const getLocationDisplayName = (locationType: any) => {
	switch (locationType) {
		case 'integrations:google:meet':
			return 'Google Meet';
		case 'integrations:zoom':
			return 'Zoom';
		case 'integrations:daily':
			return 'Cal.com';
		default:
			return locationType?.split(':').pop() || locationType;
	}
};

const getLocationIcon = (locationType: string) => {
	switch (locationType) {
		case 'integrations:google:meet':
			return <img src={GoogleMeetLogo} alt="Google Meet" className="h-10" />;
		case 'integrations:zoom':
			return <img src={ZoomLogo} alt="Zoom" className="h-10" />;
		case 'integrations:daily':
			return <img src={CalComLogo} alt="Cal.com" className="h-10" />;
		default:
			return <Video className="w-5 h-5" />;
	}
};

export const CalendarWorkflowComponent: React.FC<CalendarWorkflowComponentProps> = ({
	calendar,
	workflowNumber,
	calendarWorkflows,
	onRefetch,
}) => {
	const additionalUserInfoFields = calendar?.additionalUserInfoFields || [];

	return (
		<Card className="border-2 shadow-md">
			<div className="flex flex-col">
				<div className="flex justify-between items-start mb-6">
					<div className="mb-4">
						<Text strong className="text-lg text-gray-800">
							Workflow {workflowNumber}
						</Text>
					</div>
					<div className="flex items-start">
						<CalendarWorkflowActions
							calendar={calendar}
							calendarWorkflows={calendarWorkflows}
							onRefetch={onRefetch}
						/>
					</div>
				</div>

				<div className="space-y-4">
					<div className="border-b pb-4">
						<Text strong className="block mb-3 text-gray-700">
							Scenario
						</Text>
						<div className="bg-gray-100 p-4 rounded-lg text-sm text-gray-800 border">
							{calendar.scenario || 'No scenario description'}
						</div>
					</div>

					{calendar.nameOfCalendar === ECalendar.GOHIGHLEVEL_CALENDAR ? (
						<div className="border-b pb-4">
							<Text strong className="block mb-3 text-gray-700">
								Calendar Selection
							</Text>
							<div className="bg-gray-100 p-4 rounded-lg text-sm text-gray-800 border">
								{`${calendar.calendarSelected || 'Not set'} (${calendar.eventTypeId || 'No ID'})`}
							</div>
						</div>
					) : (
						<div className="border-b pb-4">
							<Text strong className="block mb-3 text-gray-700">
								Event Type
							</Text>
							<div className="bg-gray-100 p-4 rounded-lg text-sm text-gray-800 border">
								{calendar.eventTypeId || 'Not set'}
							</div>
						</div>
					)}

					<div className="border-b pb-4">
						<Text strong className="block mb-3 text-gray-700">
							Meeting Location
						</Text>
						<div className="bg-gray-100 p-4 rounded-lg text-sm text-gray-800 border">
							<div className="flex items-center gap-2">
								{calendar.nameOfCalendar === ECalendar.GOHIGHLEVEL_CALENDAR ? (
									<>
										<Calendar className="w-5 h-5 text-blue-500" />
										<span>GoHighLevel Calendar</span>
									</>
								) : (
									<>
										{getLocationIcon(calendar.location)}
										<span>{getLocationDisplayName(calendar.location)}</span>
									</>
								)}
							</div>
						</div>
					</div>

					<div className="border-b pb-4">
						<Text strong className="block mb-3 text-gray-700">
							Notification Preferences
						</Text>
						<div className="bg-gray-100 p-4 rounded-lg text-sm text-gray-800 border">
							<div className="space-y-2">
								<div className="flex items-center justify-between">
									<span>Send SMS to customers</span>
									<Switch
										checked={calendar.sendCallerSMSNotification !== false}
										disabled
										size="small"
									/>
								</div>
								<div className="flex items-center justify-between">
									<span>Send SMS to me</span>
									<Switch
										checked={calendar.sendOwnerSMSNotification !== false}
										disabled
										size="small"
									/>
								</div>
							</div>
						</div>
					</div>

					<div className="border-b pb-4">
						<Text strong className="block mb-3 text-gray-700">
							Booking Fields
						</Text>
						<div className="bg-gray-100 p-4 rounded-lg text-sm text-gray-800 border">
							<div className="space-y-4">
								<div>
									<div className="font-medium mb-2 text-blue-700 flex items-center">
										<span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2" />
										Default Required
									</div>
									<div className="flex flex-wrap gap-2 ml-4">
										<span className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-md text-xs font-medium">
											Attendee's Name
										</span>
										<span className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-md text-xs font-medium">
											Event Date
										</span>
										<span className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-md text-xs font-medium">
											Event Time
										</span>
										{calendar.location &&
											['attendeeAddress', 'attendeeDefined', 'attendeeInPerson'].includes(
												calendar.location,
											) && (
												<span className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-md text-xs font-medium">
													Address
												</span>
											)}
										{calendar.location === 'attendeePhone' && (
											<span className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-md text-xs font-medium">
												Phone
											</span>
										)}
									</div>
								</div>

								<div className="pt-1">
									<div className="font-medium mb-2 text-gray-700 flex items-center">
										<span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-2" />
										Additional User Information
									</div>
									<div className="flex flex-wrap gap-2 ml-4">
										<span
											className={`px-2 py-1 rounded-md text-xs font-medium flex items-center ${
												additionalUserInfoFields.includes('email')
													? 'bg-green-50 border border-green-200 text-green-700'
													: 'bg-gray-50 border border-gray-200 text-gray-600'
											}`}
										>
											<span
												className={`w-2 h-2 rounded-full mr-1 ${
													additionalUserInfoFields.includes('email')
														? 'bg-green-500'
														: 'bg-gray-400'
												}`}
											/>
											Attendee's Email
											<span className="ml-1 text-xs opacity-75">
												{additionalUserInfoFields.includes('email') ? '(Required)' : '(Optional)'}
											</span>
										</span>
										<span
											title="Display appointment times in the user's local timezone"
											className={`px-2 py-1 rounded-md text-xs font-medium flex items-center ${
												additionalUserInfoFields.includes('timezone')
													? 'bg-green-50 border border-green-200 text-green-700'
													: 'bg-gray-50 border border-gray-200 text-gray-600'
											}`}
										>
											<span
												className={`w-2 h-2 rounded-full mr-1 ${
													additionalUserInfoFields.includes('timezone')
														? 'bg-green-500'
														: 'bg-gray-400'
												}`}
											/>
											Attendee's Timezone
											<span className="ml-1 text-xs opacity-75">
												{additionalUserInfoFields.includes('timezone')
													? '(Required)'
													: '(Optional)'}
											</span>
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>

					{calendar.customFields && calendar.customFields.length > 0 && (
						<div className="pb-2">
							<Text strong className="mb-3 text-gray-700 flex items-center">
								<span className="inline-block w-4 h-4 mr-2 text-blue-500">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
									>
										<rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
										<line x1="9" y1="9" x2="15" y2="9" />
										<line x1="9" y1="12" x2="15" y2="12" />
										<line x1="9" y1="15" x2="13" y2="15" />
									</svg>
								</span>
								Custom Fields{' '}
								<span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
									{calendar.customFields.length}
								</span>
							</Text>
							<div className="bg-gray-100 p-4 rounded-lg text-sm text-gray-800 border">
								<div className="space-y-3">
									{calendar.customFields.map((field: any, fieldIndex: number) => (
										<div key={fieldIndex} className="flex gap-3">
											<span className="font-semibold text-blue-600">{fieldIndex + 1}.</span>
											<div>
												<span className="font-medium">{field.name}</span>
												{field.description && (
													<div className="text-gray-600 text-xs mt-1">{field.description}</div>
												)}
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</Card>
	);
};
